<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { getMachineLayout, listMachineLayouts } from '../productService'
import type { MachineLayoutDetail, MachineLayoutNode, MachineLayoutSummary } from '../productService'

const client = useApiClient()
const { t } = useI18n()
const layouts = ref<MachineLayoutSummary[]>([])
const selectedCode = ref('')
const detail = ref<MachineLayoutDetail | null>(null)
const loading = ref(true)
const error = ref('')

const scale = computed(() => Math.min(1, 1200 / Math.max(detail.value?.layout.width ?? 1200, 1)))
const mapStyle = computed(() => ({
  width: `${detail.value?.layout.width ?? 1200}px`,
  height: `${detail.value?.layout.height ?? 700}px`,
  transform: `scale(${scale.value})`,
}))
function pair(value: string, fallback: number): [number, number] {
  const values = value.trim().split(/[ ,]+/).map(Number)
  return [Number.isFinite(values[0]) ? values[0] : 0, Number.isFinite(values[1]) ? values[1] : fallback]
}
function nodeStyle(node: MachineLayoutNode) {
  const [x, y] = pair(node.loc, 0)
  const [width, height] = pair(node.size, 44)
  return { left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` }
}
function stateClass(node: MachineLayoutNode) {
  if (node.category !== 'machine') return `node-${node.category}`
  if (['down', 'blocked'].includes(node.state ?? '')) return 'is-danger'
  if (['maintenance', 'off'].includes(node.state ?? '')) return 'is-maintenance'
  return node.state === 'running' ? 'is-active' : 'is-idle'
}
async function selectLayout() {
  if (!selectedCode.value) return
  loading.value = true
  error.value = ''
  try { detail.value = await getMachineLayout(client, selectedCode.value) }
  catch (cause) { error.value = formatApiError(cause) }
  finally { loading.value = false }
}
async function load() {
  loading.value = true
  error.value = ''
  try {
    layouts.value = await listMachineLayouts(client)
    selectedCode.value = layouts.value[0]?.code ?? ''
    if (selectedCode.value) detail.value = await getMachineLayout(client, selectedCode.value)
  } catch (cause) { error.value = formatApiError(cause) }
  finally { loading.value = false }
}
onMounted(load)
</script>

<template>
  <div class="page-heading">
    <div><p class="eyebrow">{{ t('engineering.shopfloorMap.eyebrow') }}</p><h2>{{ t('engineering.shopfloorMap.title') }}</h2><p>{{ t('engineering.shopfloorMap.description') }}</p></div>
    <label v-if="layouts.length"><span>{{ t('engineering.shopfloorMap.layout') }}</span><select v-model="selectedCode" @change="selectLayout"><option v-for="layout in layouts" :key="layout.code" :value="layout.code">{{ layout.name }}</option></select></label>
  </div>
  <p v-if="error" class="message error" role="alert">{{ error }}</p>
  <div v-if="loading" class="state">{{ t('engineering.shopfloorMap.loading') }}</div>
  <div v-else-if="!layouts.length" class="state">{{ t('engineering.shopfloorMap.empty') }}</div>
  <section v-else-if="detail" class="map-card">
    <div class="map-viewport" :style="{ height: `${detail.layout.height * scale}px` }">
      <div class="floor-map" :style="mapStyle">
        <div v-for="node in detail.nodes" :key="node.id" class="map-node" :class="[node.category, stateClass(node)]" :style="nodeStyle(node)" :title="node.machine_name || node.label">
          <strong>{{ node.machine_code || node.label }}</strong><span v-if="node.category === 'machine'">{{ node.machine_name }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.page-heading{display:flex;align-items:end;justify-content:space-between;gap:18px;margin-bottom:20px}.page-heading p{margin:7px 0 0;color:#66726e}.eyebrow{font-size:12px;font-weight:700;text-transform:uppercase}.page-heading label{display:grid;min-width:240px;gap:5px}.page-heading label span{font-size:12px;font-weight:700;text-transform:uppercase}select{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}.map-card{overflow:hidden;padding:14px;border:1px solid #d7dfdc;border-radius:10px;background:#fff}.map-viewport{overflow:auto;border-radius:8px;background:#071521}.floor-map{position:relative;transform-origin:0 0;background-color:#071521;background-image:linear-gradient(rgb(64 104 128 / 20%) 1px,transparent 1px),linear-gradient(90deg,rgb(64 104 128 / 20%) 1px,transparent 1px);background-size:20px 20px}.map-node{position:absolute;box-sizing:border-box;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:3px;border:1px solid #315064;color:#d8e7ee;font-size:11px}.map-node.machine{flex-direction:column;border-radius:5px;color:#172c25}.map-node strong,.map-node span{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.is-active{border-color:#2f7d55;background:#c9edd8}.is-idle{background:#ecefed}.is-danger{background:#f8d7da}.is-maintenance{background:#dce6f8}.node-zone,.node-label{background:#102635}.state{display:grid;min-height:280px;place-items:center;color:#697671}.message{padding:12px;border-radius:8px}.error{color:#852d22;background:#fff5f3}@media(max-width:700px){.page-heading{align-items:stretch;flex-direction:column}.page-heading label{min-width:0}.map-card{padding:8px}}
</style>
